import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

import {
  useSelector,
} from "react-redux";

import {
  useCart,
} from "../../components/CartContext";

import "./css/Header.css";


function Header() {

  /* =====================================================
     ROUTER
  ===================================================== */

  const navigate = useNavigate();

  const location = useLocation();

  const {
    restaurantId,
    tableId,
  } = useParams();


  /* =====================================================
     MOBILE MENU
  ===================================================== */

  const [
    menuOpen,
    setMenuOpen,
  ] = useState(false);


  /* =====================================================
     CART
  ===================================================== */

  const {
    cartCount,
  } = useCart();


  /* =====================================================
     RESTAURANT NAME
  ===================================================== */

  const restaurantName =
    useSelector(
      (state) =>
        state.menu.restaurantName
    ) || "SAFFRON EMBER";


  /* =====================================================
     BASE HOME PATH
  ===================================================== */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  /* =====================================================
     CLOSE MOBILE MENU
  ===================================================== */

  const closeMenu = () => {

    setMenuOpen(false);

  };


  /* =====================================================
     HOME
  ===================================================== */

  const goToHome = () => {

    closeMenu();


    /*
      Already on Home
    */

    if (
      location.pathname === homePath
    ) {

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }


    /*
      Coming from another screen
    */

    navigate(homePath);

  };


  /* =====================================================
     CART
  ===================================================== */

  const goToCart = () => {

    closeMenu();

    navigate(
      `${homePath}/cart`
    );

  };


  /* =====================================================
     SECTION NAVIGATION
  ===================================================== */

  const scrollToSection = (
    sectionId
  ) => {

    closeMenu();


    /*
      If we are already on Home,
      find the section directly.
    */

    if (
      location.pathname === homePath
    ) {

      const element =
        document.getElementById(
          sectionId
        );


      if (element) {

        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

      }

      return;
    }


    /*
      If we are on Cart,
      Checkout,
      Order,
      Payment,
      Success, etc.

      Go back to Home and pass
      the section in the URL hash.
    */

    navigate(
      `${homePath}#${sectionId}`
    );

  };


  /* =====================================================
     HANDLE SECTION AFTER RETURNING HOME
  ===================================================== */

  useEffect(() => {

    /*
      Only process the hash on Home.
    */

    if (
      location.pathname !== homePath
    ) {
      return;
    }


    const sectionId =
      location.hash.replace(
        "#",
        ""
      );


    if (!sectionId) {
      return;
    }


    /*
      Wait for HomeScreen to render.
    */

    const timer =
      setTimeout(() => {

        const element =
          document.getElementById(
            sectionId
          );


        if (element) {

          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });

        }

      }, 150);


    return () => {

      clearTimeout(timer);

    };

  }, [
    location.pathname,
    location.hash,
    homePath,
  ]);


  /* =====================================================
     MOBILE MENU TOGGLE
  ===================================================== */

  const toggleMobileMenu = () => {

    setMenuOpen(
      (previous) =>
        !previous
    );

  };


  /* =====================================================
     HEADER
  ===================================================== */

  return (

    <header className="theme8-header">


      <div className="theme8-header-inner">


        {/* =================================================
            LOGO
        ================================================= */}

        <button
          type="button"
          className="theme8-logo"
          onClick={goToHome}
          aria-label="Go to home"
        >

          <span className="theme8-logo-mark">
            SE
          </span>


          <span className="theme8-logo-text">
            {restaurantName}
          </span>

        </button>


        {/* =================================================
            DESKTOP NAVIGATION
        ================================================= */}

        <nav
          className={
            `theme8-navigation ${
              menuOpen
                ? "theme8-navigation-open"
                : ""
            }`
          }
        >


          {/* HOME */}

          <button
            type="button"
            onClick={goToHome}
          >
            Home
          </button>


          {/* ABOUT */}

          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme8-story"
              )
            }
          >
            About
          </button>


          {/* MENU */}

          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme8-menu"
              )
            }
          >
            Menu
          </button>


          {/* GALLERY */}

          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme8-gallery"
              )
            }
          >
            Gallery
          </button>


          {/* CONTACT */}

          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme8-contact"
              )
            }
          >
            Contact
          </button>


        </nav>


        {/* =================================================
            RIGHT SIDE
        ================================================= */}

        <div className="theme8-header-actions">


          {/* =================================================
              CART
          ================================================= */}

          <button
            type="button"
            className="theme8-header-cart"
            onClick={goToCart}
            aria-label="Open cart"
          >

            <span>
              Cart
            </span>


            <span className="theme8-header-cart-count">
              {cartCount || 0}
            </span>

          </button>


          {/* =================================================
              MOBILE MENU
          ================================================= */}

          <button
            type="button"
            className={
              `theme8-mobile-menu-button ${
                menuOpen
                  ? "theme8-mobile-menu-active"
                  : ""
              }`
            }
            onClick={
              toggleMobileMenu
            }
            aria-label={
              menuOpen
                ? "Close navigation"
                : "Open navigation"
            }
            aria-expanded={
              menuOpen
            }
          >

            <span></span>

            <span></span>

            <span></span>

          </button>


        </div>

      </div>


      {/* =====================================================
          MOBILE NAVIGATION

          If your existing CSS makes
          .theme8-navigation responsive,
          this is not needed as a separate menu.

          Your current design uses the same
          navigation element, so we keep it unchanged.
      ===================================================== */}


    </header>

  );

}


export default Header;